import pick from 'lodash/pick';
import { createSelector } from 'reselect';
import { getUserId } from '../auth/selectors';
import { NAME } from './constants';
import type { CollabSlice, CollabState, User, UserId } from './types';

export const getCollabState = (state: CollabSlice): CollabState => state[NAME];

const getContentId = (_: unknown, props: { contentId?: string }) => props.contentId;

function userSortValue(user: User) {
  return user.fullName ?? user.email;
}

function toSortedList(users: Record<UserId, User>): User[] {
  return Object.values(users).sort((a, b) => userSortValue(a).localeCompare(userSortValue(b)));
}

export const getProjectUsers = createSelector(getCollabState, (domainState) => domainState.projectUsers);

export const getProjectUsersSortedList = createSelector(getProjectUsers, (users) => toSortedList(users));

export const getContentUsers = createSelector([getCollabState, getContentId], (domainState, contentId) =>
  contentId ? domainState.contentUsers[contentId] : undefined
);

export const getEditingUserId = createSelector(getContentUsers, (contentUsers) => contentUsers?.lockUser);

export const getViewingUsersIds = createSelector(
  getContentUsers,
  (contentUsers) => contentUsers?.viewingUsers ?? new Set<UserId>()
);

export const getContentUsersIds = createSelector([getEditingUserId, getViewingUsersIds], (lockUser, viewingUsers) =>
  lockUser ? viewingUsers.add(lockUser) : viewingUsers
);

export const getOtherContentUsersIds = createSelector(
  [getContentUsersIds, getUserId],
  (users, currentUserId) => new Set<UserId>([...users].filter((id) => id !== currentUserId))
);

export const getIsLocked = createSelector([getUserId, getEditingUserId], (currentUserId, editingUserId) =>
  Boolean(editingUserId && currentUserId !== editingUserId)
);

export const getEditingUser = createSelector([getProjectUsers, getEditingUserId], (users, editingUserId) => {
  return editingUserId ? users[editingUserId] : undefined;
});

export const getViewingUsers = createSelector([getProjectUsers, getViewingUsersIds], (projectUsers, viewingUsersIds) =>
  toSortedList(pick(projectUsers, [...viewingUsersIds]))
);
